import React, { useEffect, useState } from 'react';

import { send } from 'loot-core/src/platform/client/fetch';

import {
  REACT_APP_ZOOM_LINK,
  REACT_APP_COACH_CAL_ID,
} from '../../coaches/coachVariables';
import { theme } from '../../style';
import { isInApp } from '../billing/billing';
import { Block } from '../common/Block';
import { Button } from '../common/Button';
import {
  Modal,
  ModalCloseButton,
  ModalHeader,
  ModalTitle,
} from '../common/Modal';
import { Text } from '../common/Text';
import { View } from '../common/View';

function monthName(isoDate) {
  return new Date(`${isoDate}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
}

// "$55" for whole dollars, "$55.50" otherwise.
function priceLabel(cents) {
  if (cents == null) return null;
  return cents % 100 === 0
    ? `$${cents / 100}`
    : (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
}

export function ScheduleZoom({ modalProps }) {
  // Premium includes one session a month. The server checks the member's
  // plan and this month's bookings, so the included session can't be booked
  // twice or by someone who isn't on Premium. Any other session is paid for
  // before the booking page opens.
  const [allowance, setAllowance] = useState(null);
  const [failed, setFailed] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [paymentOpened, setPaymentOpened] = useState(false);

  useEffect(() => {
    send('billing-session-allowance', {
      url: String(window.location.href),
    }).then(result => {
      if (result.error || !result.data) {
        setFailed(true);
      } else {
        setAllowance(result.data);
      }
    });
  }, []);

  // Coaches who haven't moved to the booking calendar use their own video
  // link, as before.
  const usesCalendar = Boolean(REACT_APP_COACH_CAL_ID);

  const getCalLink = sessionType => {
    if (!usesCalendar) {
      return REACT_APP_ZOOM_LINK;
    }
    return `https://cal.mybudgetcoach.com/${REACT_APP_COACH_CAL_ID}/${sessionType}`;
  };

  // The iPhone app opens links in the phone's browser; the web opens a tab.
  const openLink = link => {
    if (isInApp()) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'openInBrowser', url: link }),
      );
    } else {
      window.open(link, '_blank');
    }
  };

  const handleScheduleClick = sessionType => {
    openLink(getCalLink(sessionType));
  };

  async function payForSession() {
    if (!usesCalendar) {
      handleScheduleClick('basic-session');
      return;
    }

    setPayError(null);
    setPaying(true);
    const inApp = isInApp();
    // Open the tab now, while the click still counts as the member's own
    // action: browsers block tabs opened after waiting for the server.
    const tab = inApp ? null : window.open('', '_blank');
    try {
      if (tab) {
        tab.document.title = 'Opening payment…';
        tab.document.body.textContent = 'Opening the secure payment page…';
      }
    } catch (e) {
      // Only a courtesy message; the payment page still opens.
    }

    const result = await send('billing-session-checkout', {
      url: String(window.location.href),
      inApp,
    });
    setPaying(false);

    if (result.error || !result.data?.url) {
      tab?.close();
      setPayError(
        result.error === 'no-coach-calendar'
          ? "Your coach's booking calendar isn't set up yet. Please contact support and we'll book your call."
          : "We couldn't open the payment page. Please try again, or contact support.",
      );
      return;
    }

    if (inApp) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'openInBrowser', url: result.data.url }),
      );
    } else if (tab) {
      tab.location.href = result.data.url;
    } else {
      // The browser blocked the new tab, so pay in this one.
      window.location.href = result.data.url;
    }
    setPaymentOpened(true);
  }

  const isPremium = allowance?.includedEligible;
  const hasIncludedLeft = isPremium && allowance.includedLeft > 0;
  // The free 30-minute session onboarding offers, if they haven't booked it.
  const freeSession = allowance?.freeSession?.available
    ? allowance.freeSession
    : null;
  const price = priceLabel(allowance?.paidSession?.amount);
  const paidNote = price
    ? `${isPremium ? 'Extra calls are' : 'Video calls with your coach are'} ${price} each, paid when you book.`
    : 'Paid when you book.';
  const freeNote = freeSession
    ? 'Your free 30-minute session with your coach is still waiting for you. '
    : '';

  let message;
  if (!allowance && !failed) {
    message = 'Checking your sessions…';
  } else if (failed) {
    message =
      "We couldn't check your included sessions just now. You can still book a paid call, or contact support and we'll sort it out.";
  } else if (hasIncludedLeft) {
    message = `${freeNote}Your plan includes one video call with your coach each month, and you haven't used this month's yet. ${paidNote}`;
  } else if (isPremium) {
    message = `${freeNote}You've used this month's included video call. Your next one is available from ${monthName(allowance.resetsOn)}. ${paidNote}`;
  } else {
    message = `${freeNote}${paidNote}`;
  }

  const payLabel = paying
    ? 'Opening payment…'
    : failed || !price
      ? 'Book a paid call'
      : isPremium
        ? `Pay ${price} for an extra call`
        : `Pay ${price} and book`;

  return (
    <Modal name="schedule-zoom">
      {({ state: { close } }) => (
        <>
          <ModalHeader
            title={<ModalTitle title="Schedule Video Call" shrinkOnOverflow />}
            rightContent={<ModalCloseButton onPress={close} />}
          />
          <View style={{ lineHeight: 1.5 }}>
            <Block>
              <Text style={{ color: theme.pageText }}>{message}</Text>
            </Block>

            {paymentOpened && (
              <Block style={{ marginTop: 12 }}>
                <Text style={{ color: theme.pageText }}>
                  Finish paying on the payment page. As soon as you have, it
                  takes you to your coach's calendar to pick a time.
                </Text>
              </Block>
            )}
            {payError && (
              <Block style={{ marginTop: 12 }}>
                <Text style={{ color: theme.errorText }}>{payError}</Text>
              </Block>
            )}

            <View
              style={{
                marginTop: 20,
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={() => {
                  window.location.href =
                    'mailto:admin@mybudgetcoach.com?subject=Support';
                }}
              >
                Contact Support
              </Button>

              {freeSession && (
                <Button type="primary" onClick={() => openLink(freeSession.url)}>
                  Book your free 30-minute session
                </Button>
              )}
              {hasIncludedLeft && (
                <Button
                  type={freeSession ? 'normal' : 'primary'}
                  onClick={() => handleScheduleClick('premium-included')}
                >
                  Schedule Included Call
                </Button>
              )}
              {(failed || allowance) && (
                <Button
                  type={hasIncludedLeft || freeSession ? 'normal' : 'primary'}
                  disabled={paying}
                  onClick={payForSession}
                >
                  {payLabel}
                </Button>
              )}
            </View>
          </View>
        </>
      )}
    </Modal>
  );
}
